import React, { ReactElement, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useSelector } from 'react-redux';
import useFundNotes, { UseFundNoteProps } from '../../hooks/useFundNotes';
import useRouteParams from '../../hooks/useRouteParams';
import {
  clientNameSelector,
  configNameSelector,
} from '../../redux/auth/selectors';
import {
  activeDateSelector,
  activePageSelector,
  activeSectionSelector,
  allPagesSelector,
  createActiveFundSelectorBySection,
  createPageDetailsSelector,
} from '../../redux/pages/selectors';
import { componentsForExportSelector } from '../../redux/fileExport/selectors';
import { CustomColumn } from '../../types/components/tables/tableTypes';
import { buildCsv, buildCsvWithTitle } from '../../utilities/csvBuilder';
import { formatDateForCheckingState } from '../../utilities/dateFormatters';
import { PdfGenerator } from '../layout/titleBar/components/FileExportButton';
import { useNavigate } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import {
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Theme,
  Tooltip,
} from '@mui/material';
import { Archive } from '@mui/icons-material';
import { PDFConfig } from '../../types/components/charts/lineCharts';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { zIndexes } from '../../styling/zIndexes';

const useStyles = makeStyles<Theme>((theme) => ({
  rootDiv: {
    marginRight: '0.12rem',
  },
  root: {
    '& svg': {
      fontSize: '1.25rem',
    },
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
  },
}));

interface AdditionalTableDetails {
  data: any[];
  columns: CustomColumn<any>[];
}

export type ColumnIdToHeaderMapType = { label: string; key: string }[];

interface Props {
  exportData: any[];
  csvTitle?: string;
  pdfIdentifier?: string;
  fields: string[];
  fieldsMap?: ColumnIdToHeaderMapType;
  fileName?: string;
  allowPdfExport?: boolean;
  allowCsvExport?: boolean;
  allowNoteExport?: UseFundNoteProps & AdditionalTableDetails;
  selectedPositionDate?: string;
  linkToRaptorExport?: boolean;
  pdfConfig?: PDFConfig;
}

const NoteExportSelection = ({
  fundName,
  subject,
  topic,
  positionDate,
  shouldCall,
  columns,
  data,
  prependWithDetail,
}: UseFundNoteProps & AdditionalTableDetails) => {
  const { notes, loading, fetchFundNotes, postFundNotes } = useFundNotes({
    fundName,
    subject,
    topic,
    positionDate,
    shouldCall,
    prependWithDetail,
  });
  const [open, setOpen] = useState(false);
  const handleNoteExport = async () => {
    const tableStringified = JSON.stringify({
      columns,
      data,
    });
    await postFundNotes('TABLE_NOTE:' + tableStringified);
    setOpen(true);
  };
  return (
    <>
      <MenuItem onClick={handleNoteExport} key="notes">
        Save to Note
      </MenuItem>
    </>
  );
};

// This needs a config prop to determine the pdf export

function ExportButton(props: React.PropsWithChildren<Props>): ReactElement {
  const navigate = useNavigate();
  const classes = useStyles();

  const componentsToExport = useSelector(componentsForExportSelector);

  //buildCsv just strips out what we don't want to keep
  const [open, setOpen] = useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const allPages = useSelector(allPagesSelector) || {};

  const currentPage = useSelector(activePageSelector);
  const currentSection = useSelector(activeSectionSelector);
  const activeFund = useSelector(
    createActiveFundSelectorBySection(currentSection),
  );
  const pageDetails = useSelector(
    createPageDetailsSelector(currentPage, currentSection),
  );
  const activeDate = useSelector(activeDateSelector);
  const routeParams = useRouteParams('positionDate');
  const handleToggle = () => {
    setOpen((prevOpen) => {
      const next = !prevOpen;
      if (next) {
        // Suppress all hover tooltips/popovers while export menu is open
        document.body.classList.add('suppress-tooltips');
      } else {
        document.body.classList.remove('suppress-tooltips');
      }
      return next;
    });
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
    // Re-enable tooltips/popovers after menu closes
    document.body.classList.remove('suppress-tooltips');
  };

  const clientName = useSelector(clientNameSelector) || 'mersenne';
  const configName = useSelector(configNameSelector) || 'mersenne_funds';

  const handlePdfExport = () => {
    // Ensure tooltips/popovers are hidden during capture
    document.body.classList.add('suppress-tooltips');
    if (props.fields.length > 23) {
      alert(
        `Sorry, the maximum number of columns for pdf export is 23.\nYou have ${props.fields.length} columns selected.`,
      );
    } else {
      new PdfGenerator({
        idenftifier: props.pdfIdentifier || props?.pdfConfig?.pdfIdentifier,
        pageTitle: pageDetails?.pageTitle || '',
        fundName: activeFund?.name || '',
        pageId: currentPage || 'pageId',
        fundId: activeFund?.id || '',
        clientName,
        configName,
        positionDate: props.selectedPositionDate,
        chartWidth: props?.pdfConfig?.chartWidth,
        analysisDate:
          activeDate || formatDateForCheckingState(new Date()) || 'today',
      }).generatePdf(componentsToExport);
    }
    // Best-effort restore after generation window
    window.setTimeout(() => {
      document.body.classList.remove('suppress-tooltips');
    }, 2000);
  };

  // Safety: cleanup on unmount
  React.useEffect(() => {
    return () => {
      document.body.classList.remove('suppress-tooltips');
    };
  }, []);

  const csvData = props.csvTitle
    ? buildCsvWithTitle(props.csvTitle, props.fields, props.exportData)
    : buildCsv(props.fields, props.exportData);

  function goRaptorExport() {
    const raptorExportUrl = `/${currentSection}/raptor-export`;
    navigate(raptorExportUrl);
  }

  const generateCsvFilename = () => {
    if (props.fileName) {
      return props.fileName;
    } else {
      // Adepa in typical fashion has a different way of naming files
      // They always want the date to be the current date, not the active date
      // This is a hack to get around that
      // We should probably refactor this to be more flexible, ie have a client setting for allowing the client to choose whether to use the active date or the current date
      // Additionally, the entire export functionality should be refactored to be more flexible, allowing for more customization by client of file name etc
      if (clientName === 'adepa') {
        return `${currentPage}_${formatDateForCheckingState(new Date())}`;
      } else {
        return `${currentPage}_${routeParams?.positionDate || activeDate || formatDateForCheckingState(new Date())}`;
      }
    }
  };

  function exportToExcel() {
    const worksheet = XLSX.utils.json_to_sheet(
      csvData as Record<string, any>[],
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(blob, `${generateCsvFilename()}.xlsx`);
  }

  return (
    <>
      <div ref={anchorRef} style={{ position: 'relative' }}>
        <Tooltip
          classes={{ tooltip: classes.tooltip }}
          title="Export"
          disableFocusListener
          disableTouchListener
        >
          <IconButton
            classes={{ root: classes.root }}
            onClick={handleToggle}
            aria-haspopup
            aria-expanded={open ? 'true' : 'false'}
            aria-controls="export-menu"
          >
            <Archive />
          </IconButton>
        </Tooltip>
        <Popper
          style={{ zIndex: zIndexes.exportMenu }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          placement="bottom-end"
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                padding: 8,
              },
            },
            {
              // Ensure the menu captures pointer events and doesn't leak to charts beneath
              name: 'eventListeners',
              enabled: true,
              options: { scroll: true, resize: true },
            },
          ]}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper
                elevation={3}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <ClickAwayListener
                  onClickAway={handleClose}
                  mouseEvent="onMouseDown"
                >
                  <MenuList id="export-menu">
                    {props.allowCsvExport !== false && (
                      <>
                        <MenuItem onClick={exportToExcel} key={'excel'}>
                          Excel
                        </MenuItem>
                        <CSVLink
                          data={csvData}
                          headers={props.fieldsMap}
                          target="_blank"
                          filename={generateCsvFilename()}
                        >
                          <MenuItem key={'csv'}>CSV</MenuItem>
                        </CSVLink>
                      </>
                    )}

                    {props.allowPdfExport && (
                      <MenuItem onClick={handlePdfExport} key="pdf">
                        PDF
                      </MenuItem>
                    )}
                    {props.allowNoteExport && (
                      <NoteExportSelection {...props.allowNoteExport} />
                    )}
                    {'raptor-export' in allPages &&
                      props.linkToRaptorExport && (
                        <MenuItem onClick={goRaptorExport} key="raptor-export">
                          Raptor Export
                        </MenuItem>
                      )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </>
  );
}

export default ExportButton;
